import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { apiClient } from '../api/apiClient';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/PageShell';
import { CornerCard, PrimaryButton, GhostButton, Tag, Muted } from '../components/ui';
import { color, font } from '../theme/tokens';

// --- Animations ---
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// --- Styled Components ---

const Container = styled(CornerCard)`
    max-width: 800px;
    margin: 0 auto;
    color: ${color.text};
    animation: ${fadeIn} 0.45s ease-out;
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 28px;
`;

const Title = styled.h2`
    margin: 0 0 14px;
    font-family: ${font.display};
    font-weight: 800;
    font-size: 28px;
    letter-spacing: 0.2em;
    color: ${color.textBright};
    .count { margin-left: 14px; font-family: ${font.mono}; font-weight: 400; font-size: 16px; letter-spacing: 0.04em; color: ${color.text2}; }
`;

const Description = styled.p`
    margin: 0;
    font-size: 15px;
    line-height: 1.9;
    color: ${color.text2};
    b { font-family: ${font.mono}; font-weight: 500; color: ${color.text}; }
`;

const CategorySelector = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    margin: 36px 0 40px;
`;

const CategoryButton = styled.button`
    min-height: 40px;
    padding: 0 20px;
    border-radius: 0;
    border: 1px solid ${color.line2};
    background-color: transparent;
    color: ${color.text2};
    font-family: ${font.body};
    font-size: 14px;
    letter-spacing: 0.12em;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

    &:hover {
        border-color: ${color.bloodHi};
        color: ${color.text};
    }

    ${({ $selected }) => $selected && css`
        &, &:hover {
            background-color: ${color.blood};
            border-color: ${color.blood};
            color: ${color.onBlood};
        }
    `}
`;

const QuestionCard = styled.div`
    margin-top: 8px;
    padding: 28px;
    border: 1px solid ${color.line};
    background: rgba(7, 2, 5, 0.5);

    @media (max-width: 640px) { padding: 20px 16px; }
`;

const QuestionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 18px;
`;

const QuestionText = styled.p`
    margin: 0 0 24px;
    font-size: 18px;
    line-height: 1.8;
    color: ${color.text};
`;

const OptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const OptionLabel = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 14px 16px;
    border: 1px solid ${color.line};
    background-color: ${color.surface};
    color: ${color.text2};
    font-size: 15px;
    line-height: 1.7;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;

    &:hover {
        border-color: ${color.line2};
        background-color: ${color.surface2};
        color: ${color.text};
    }

    &:focus-within {
        outline: 2px solid ${color.bloodHi};
        outline-offset: 2px;
    }

    .letter { font-family: ${font.mono}; color: ${color.text3}; margin-right: 8px; }

    ${({ $checked }) => $checked && css`
        border-color: rgba(224, 70, 79, 0.6);
        background-color: rgba(200, 50, 62, 0.1);
        color: ${color.text};
        .letter { color: ${color.bloodHi}; }
    `}
`;

const CustomCheckbox = styled.span`
    flex-shrink: 0;
    box-sizing: border-box;
    width: 18px;
    height: 18px;
    margin-top: 0.3em;
    border: 1px solid ${color.text3};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    ${({ $checked }) => $checked && css`
        border-color: ${color.blood};
        background-color: ${color.blood};
        &::after {
            content: '';
            width: 8px;
            height: 4px;
            margin-top: -2px;
            border-left: 1.5px solid ${color.onBlood};
            border-bottom: 1.5px solid ${color.onBlood};
            transform: rotate(-45deg);
        }
    `}
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
`;

const NavButtons = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 14px;
    margin-top: 32px;
`;

const ResultCard = styled.div`
    text-align: center;
    padding: 16px 0;
`;

const ResultTitle = styled.h2`
    margin: 20px 0 16px;
    font-family: ${font.display};
    font-weight: 800;
    font-size: 36px;
    letter-spacing: 0.24em;
    color: ${props => props.$passed ? color.textBright : color.bloodHi};
    text-shadow: 0 0 24px rgba(200, 50, 62, 0.35);
`;

const ResultScore = styled.p`
    margin: 28px 0 12px;
    font-size: 15px;
    letter-spacing: 0.2em;
    color: ${color.text3};
    b {
        display: block;
        margin-top: 8px;
        font-family: ${font.mono};
        font-weight: 500;
        font-size: 56px;
        letter-spacing: 0.02em;
        color: ${color.textBright};
    }
    span { font-family: ${font.mono}; font-size: 20px; color: ${color.text3}; letter-spacing: 0.02em; }
`;

const ErrorMessage = styled.p`
    margin: 0 0 16px;
    padding: 12px 16px;
    border-left: 2px solid ${color.bloodHi};
    background-color: rgba(200, 50, 62, 0.08);
    color: ${color.bloodHi};
    font-size: 14px;
    text-align: center;
`;

// --- Component ---

function Assessment() {
    // ... (Your existing hooks and logic remain exactly the same)
    const navigate = useNavigate();
    const [stage, setStage] = useState('select'); // 'select', 'exam', 'result'
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState({}); // { questionId: [answerIndex] }
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await apiClient.get('/user/assessment/categories');
                setCategories(response.data);
            } catch (err) {
                setError('无法加载题库分类，请稍后重试。');
            }
        };
        fetchCategories();
    }, []);

    const toggleCategory = (category) => {
        setSelectedCategories(prev =>
            prev.includes(category)
                ? prev.filter(c => c !== category)
                : [...prev, category].length <= 3 ? [...prev, category] : prev
        );
    };

    const startExam = async () => {
        if (selectedCategories.length === 0) {
            setError('请至少选择一个题库。');
            return;
        }
        setError('');
        try {
            const response = await apiClient.post('/user/assessment/start', { categories: selectedCategories });
            setQuestions(response.data);
            setStage('exam');
        } catch (err) {
            setError(err.response?.data?.message || '开始考试失败。');
        }
    };

    const handleAnswerSelect = (questionId, optionIndex) => {
        setUserAnswers(prev => {
            const currentAnswers = prev[questionId] || [];
            const newAnswers = currentAnswers.includes(optionIndex)
                ? currentAnswers.filter(a => a !== optionIndex)
                : [...currentAnswers, optionIndex];
            return { ...prev, [questionId]: newAnswers.sort() };
        });
    };

    const submitExam = async () => {
        if (window.confirm('确认提交答卷吗？')) {
            const formattedAnswers = questions.map(q => ({
                questionId: q.id,
                answer: userAnswers[q.id] || [],
            }));
            
            try {
                const response = await apiClient.post('/user/assessment/submit', { answers: formattedAnswers });
                setResult(response.data);
                setStage('result');
            } catch (err) {
                setError(err.response?.data?.message || '提交失败。');
            }
        }
    };

    if (stage === 'select') {
        return (
            <PageShell title="試問" en="ASSESSMENT">
                <Container>
                    <Header>
                        <Title>转正考核</Title>
                        <Description>请选择 <b>1-3</b> 个你擅长的领域进行考核。系统将从你选择的题库中随机抽取 <b>40</b> 道题，总分高于 <b>90</b> 分即可通过。</Description>
                    </Header>
                    {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
                    <CategorySelector role="group" aria-label="题库分类">
                        {categories.map(cat => (
                            <CategoryButton
                                key={cat}
                                type="button"
                                $selected={selectedCategories.includes(cat)}
                                aria-pressed={selectedCategories.includes(cat)}
                                onClick={() => toggleCategory(cat)}
                            >
                                {cat}
                            </CategoryButton>
                        ))}
                    </CategorySelector>
                    <div style={{ textAlign: 'center' }}>
                        <PrimaryButton type="button" onClick={startExam} disabled={selectedCategories.length === 0}>开始考核</PrimaryButton>
                    </div>
                </Container>
            </PageShell>
        );
    }

    if (stage === 'exam' && questions.length > 0) {
        const currentQuestion = questions[currentQuestionIndex];
        const isAnswered = (userAnswers[currentQuestion.id] || []).length > 0;
        return (
            <PageShell title="試問" en="ASSESSMENT">
                <Container>
                    <Header>
                        <Title>考核进行中<span className="count">{currentQuestionIndex + 1} / {questions.length}</span></Title>
                    </Header>
                    {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
                    <QuestionCard>
                        <QuestionHeader>
                            <Tag>{currentQuestion.category}</Tag>
                        </QuestionHeader>
                        <QuestionText>{currentQuestion.question_text}</QuestionText>
                        <OptionsContainer>
                            {currentQuestion.options.map((option, index) => {
                                const isChecked = (userAnswers[currentQuestion.id] || []).includes(index);
                                return (
                                    <OptionLabel key={index} $checked={isChecked}>
                                        <HiddenCheckbox
                                            checked={isChecked}
                                            onChange={() => handleAnswerSelect(currentQuestion.id, index)}
                                        />
                                        <CustomCheckbox $checked={isChecked} aria-hidden="true" />
                                        <span><span className="letter">{String.fromCharCode(65 + index)}.</span>{option}</span>
                                    </OptionLabel>
                                )
                            })}
                        </OptionsContainer>
                    </QuestionCard>
                    <NavButtons>
                        <GhostButton type="button" onClick={() => setCurrentQuestionIndex(prev => prev - 1)} disabled={currentQuestionIndex === 0}>上一题</GhostButton>
                        {currentQuestionIndex === questions.length - 1 ? (
                            <PrimaryButton type="button" onClick={submitExam} disabled={!isAnswered}>提交答卷</PrimaryButton>
                        ) : (
                            <PrimaryButton type="button" onClick={() => setCurrentQuestionIndex(prev => prev + 1)} disabled={!isAnswered}>下一题</PrimaryButton>
                        )}
                    </NavButtons>
                </Container>
            </PageShell>
        );
    }

    if (stage === 'result' && result) {
        return (
            <PageShell title="試問" en="ASSESSMENT">
                <Container>
                    <ResultCard>
                        <Tag $solid={result.passed}>{result.passed ? '通过' : '未通过'}</Tag>
                        <ResultTitle $passed={result.passed}>
                            {result.passed ? '恭喜通过！' : '未能通过'}
                        </ResultTitle>
                        <Description>{result.message}</Description>
                        <ResultScore>
                            你的分数
                            <b>{result.score} <span>/ 100</span></b>
                        </ResultScore>
                        <Description>答对 <b>{result.correctCount}</b> / <b>{result.totalQuestions}</b> 题</Description>
                        <PrimaryButton type="button" onClick={() => navigate('/profile')} style={{ marginTop: '36px' }}>返回个人中心</PrimaryButton>
                    </ResultCard>
                </Container>
            </PageShell>
        );
    }

    return (
        <PageShell title="試問" en="ASSESSMENT">
            <Muted>载入中…</Muted>
        </PageShell>
    );
}

export default Assessment;
